import { Link } from "react-router-dom";
import { CARRIER_COORDINATION as T } from "@/constants/testIds";
import {
  Ship, Container, Anchor, CheckCircle2, Clock, FileCheck2, ArrowUpRight,
} from "lucide-react";

function StatusPill({ tone = "green", children }) {
  const map = {
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
    slate: "bg-slate-50 text-slate-700 ring-slate-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${map[tone]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}

function DashboardMock() {
  return (
    <div
      data-testid={T.heroDashboard}
      className="relative w-full rounded-2xl border border-slate-line bg-white shadow-card-lg animate-fade-up stagger-4"
      role="img"
      aria-label="FreightIQ Workspace shipment coordination dashboard preview"
    >
      <div className="flex items-center gap-2 border-b border-slate-line px-5 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-red-300/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-300/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-300/70" />
        <span className="ml-3 font-mono text-[11px] text-slate-500">
          freightiq.demaxtore.app / shipments / MSCU1234567
        </span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-blue-soft px-2 py-0.5 text-[10px] font-medium text-blue-brand">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-brand animate-pulse-soft" />
          Live
        </span>
      </div>

      <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-3">
          <div className="rounded-xl border border-slate-line p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Shipment</p>
                <p className="mt-1 font-mono text-[13px] text-navy">SHP-2024-00892 · Coffee Grade AA</p>
              </div>
              <StatusPill tone="green">On Schedule</StatusPill>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Carrier" value="MSC" />
              <Field label="Vessel" value="MSC Aurora" />
              <Field label="Container" value="MSCU1234567" mono />
              <Field label="ETA" value="12 Jun" />
            </div>

            <div className="mt-5 flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                <Anchor className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-[12px] font-medium text-navy">Mersin</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">POL</span>
              </div>
              <div className="relative flex-1">
                <div className="h-[2px] w-full bg-slate-200" />
                <div className="absolute left-0 top-0 h-[2px] bg-blue-brand" style={{ width: "62%" }} />
                <span className="absolute -top-[6px] flex h-3 w-3 items-center justify-center rounded-full bg-white ring-2 ring-blue-brand" style={{ left: "62%" }}>
                  <Ship className="h-2 w-2 text-blue-brand" />
                </span>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                <Anchor className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-[12px] font-medium text-navy">Mombasa</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">POD</span>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <MicroCard
              icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
              label="Booking Status"
              value="Confirmed"
              pill={<StatusPill tone="green">MSC · #B-58211</StatusPill>}
            />
            <MicroCard
              icon={<Container className="h-4 w-4 text-amber-600" />}
              label="Container Released"
              value="Pending"
              pill={<StatusPill tone="amber">Depot approval</StatusPill>}
            />
            <MicroCard
              icon={<FileCheck2 className="h-4 w-4 text-blue-brand" />}
              label="Documentation"
              value="Ready"
              pill={<StatusPill tone="blue">B/L · COO</StatusPill>}
            />
          </div>
        </div>

        <div className="rounded-xl border border-slate-line lg:col-span-2">
          <div className="flex items-center justify-between border-b border-slate-line px-4 py-3">
            <p className="text-[11px] uppercase tracking-widest text-slate-500">Notifications</p>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">4 today</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {[
              { icon: <Clock className="h-3.5 w-3.5 text-emerald-600" />, title: "Booking Confirmed", detail: "MSC · Ref B-58211", time: "9m" },
              { icon: <Ship className="h-3.5 w-3.5 text-blue-brand" />, title: "ETA Updated", detail: "MSC Aurora → 12 Jun", time: "32m" },
              { icon: <Container className="h-3.5 w-3.5 text-amber-600" />, title: "Container Released", detail: "MSCU1234567", time: "1h" },
              { icon: <FileCheck2 className="h-3.5 w-3.5 text-slate-600" />, title: "Documents Ready", detail: "B/L · Invoice · COO", time: "2h" },
            ].map((n, i) => (
              <li key={i} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-slate-50">{n.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-navy">{n.title}</p>
                  <p className="truncate text-[12px] text-slate-500">{n.detail}</p>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">{n.time}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-0.5 text-[13px] text-navy ${mono ? "font-mono" : "font-medium"}`}>{value}</p>
    </div>
  );
}

function MicroCard({ icon, label, value, pill }) {
  return (
    <div className="rounded-xl border border-slate-line bg-white p-3.5">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-50">{icon}</span>
        <p className="text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      </div>
      <p className="mt-2 text-[15px] font-semibold text-navy">{value}</p>
      <div className="mt-2">{pill}</div>
    </div>
  );
}

export default function Hero() {
  return (
    <section data-testid={T.hero} className="relative isolate overflow-hidden bg-white">
      <div className="pointer-events-none absolute inset-0 bg-hero-grid opacity-70" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -z-10 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-blue-soft blur-3xl opacity-60" />

      <div className="mx-auto max-w-7xl px-6 pb-20 pt-16 lg:px-10 lg:pt-24">
        <div className="mx-auto max-w-4xl text-center">
          <p
            data-testid={T.heroEyebrow}
            className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-slate-line bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-brand shadow-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-blue-brand" />
            Import Operations
          </p>

          <h1
            data-testid={T.heroHeadline}
            className="animate-fade-up stagger-1 mt-6 text-4xl font-semibold text-navy sm:text-5xl lg:text-6xl"
          >
            Carrier Coordination.
            <br />
            <span className="text-slate-400">Without the Chaos.</span>
          </h1>

          <p
            data-testid={T.heroSubheadline}
            className="animate-fade-up stagger-2 mx-auto mt-6 max-w-2xl text-lg text-slate-600 sm:text-xl"
          >
            Coordinate carriers, container bookings and shipment schedules from one Workspace.
          </p>

          <div
            data-testid={T.heroDescription}
            className="animate-fade-up stagger-3 mx-auto mt-6 max-w-2xl space-y-3 text-[15px] leading-relaxed text-slate-500"
          >
            <p>Managing freight should not require endless emails, phone calls and spreadsheets.</p>
            <p>
              <span className="font-semibold text-navy">FreightIQ™</span> centralizes carrier coordination, booking management and shipment visibility inside one Workspace.
            </p>
          </div>

          <div className="animate-fade-up stagger-4 mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/freightiq"
              data-testid={T.heroPrimaryCta}
              className="group inline-flex items-center gap-2 rounded-full bg-blue-brand px-5 py-3 text-sm font-medium text-white shadow-card transition hover:translate-y-[-1px] hover:shadow-card-lg"
              style={{ backgroundColor: "hsl(var(--blue))" }}
            >
              Explore FreightIQ™
              <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/workspace"
              data-testid={T.heroSecondaryCta}
              className="inline-flex items-center gap-2 rounded-full border border-slate-line bg-white px-5 py-3 text-sm font-medium text-navy shadow-sm transition hover:border-slate-300 hover:shadow-card"
            >
              Create Your Free Workspace
            </Link>
          </div>
        </div>

        <div className="relative mx-auto mt-14 max-w-6xl">
          <DashboardMock />
          <div className="pointer-events-none mx-auto mt-2 h-10 w-[80%] rounded-full bg-slate-200/50 blur-2xl" />
        </div>
      </div>
    </section>
  );
}
